import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import {
  GetUserInfo,
  SortOrder,
  PostUserInfo,
  PutUserInfo,
} from '../../services/apiTypes';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../stores/store';
import UserRegisterModal from './components/UserRegisterModal';
import UserSearchForm from './components/UserSearchForm';
import UsersTable, { SortKeyType } from './components/UsersTable';
import {
  getUser,
  createUser,
  deleteUser,
  updateUser,
} from '../../stores/userManagement/slice';
import UserEditModal from './components/UserEditModal';
import { UserSearchParam } from '../../stores/userManagement/api';
import AddButton from '../../_common/Button/AddButton';
import { fetchAccountsInfo } from '../../stores/accountsInfo/slice';
import { isAdmin as isAdministrator } from '../../_utils/utils';
import ConfirmModal from '../../_common/Modal/ConfirmModal';
import { useTranslation } from 'react-i18next';
import useErrorModal from '../../hooks/useErrorModal';
import ErrorModal from '../../_common/Modal/ErrorModal';
import { isFulfilled, isRejected } from '@reduxjs/toolkit';
import {
  isGetAccountsInfo,
  isGetUserInfoList,
} from '../../_utils/typeGuardUtils';
import { API_CODE } from '../../_constants/apiConst';

const defaultParams: UserSearchParam = {
  userId: '',
  name: '',
  roleId: -1,
  companyKey: '',
};

const ManageUser: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();

  const [isOpenModal, setIsOpenModal] = useState([false, false]);
  const [userList, setUserList] = useState<GetUserInfo[]>([]);
  const [userUpdate, setUserUpdate] = useState<GetUserInfo>();

  const [params, setParams] = useState<UserSearchParam>(defaultParams);

  const [errorMsg, setErrorMsg] = useState('');
  const { error, showApiError, showTypeError, getApiErrorMessage, hideError } =
    useErrorModal();

  /** アカウントロック解除の確認ダイアログ */
  const [isOpenAccountUnlock, setIsOpenAccountUnlock] = useState(false);

  const { userData } = useSelector((state: RootState) => state.userInfo);

  /** メ内サ権限か */
  const isAdmin = isAdministrator(userData);

  /** ソート変更フラグ */
  const [sortChanged, setSortChanged] = useState(false);

  useEffect(() => {
    (async () => {
      const response = await dispatch(fetchAccountsInfo());
      if (isFulfilled(response)) {
        if (!isGetAccountsInfo(response.payload)) {
          showTypeError(API_CODE.ACCOUNT_INFO);
        }
      } else {
        showApiError(response.payload, API_CODE.ACCOUNT_INFO);
      }
    })();
  }, [dispatch]);

  useEffect(() => {
    fetchUserList();
    if (sortChanged) setSortChanged(false);
  }, [sortChanged]);

  const fetchUserList = async () => {
    const data = await dispatch(getUser(params));
    if (isFulfilled(data)) {
      if (isGetUserInfoList(data.payload)) {
        setUserList(data.payload);
      } else {
        showTypeError(API_CODE.USER_INFO);
      }
    } else {
      showApiError(data.payload, API_CODE.USER_INFO);
    }
  };

  /** ソート実施時 */
  const handleSortChange = (sortKey: SortKeyType, order: SortOrder) => {
    setParams((prev) => ({
      ...prev,
      sortKey,
      order,
    }));
    setSortChanged(true);
  };

  const handleDialogCreateOpen = () => {
    setIsOpenModal((prev) => {
      const newState = [...prev];
      newState[0] = true;
      return newState;
    });
  };

  const handleDialogUpdateOpen = (userId: string) => {
    const targetUser = userList.find((user) => user.userId === userId);
    if (targetUser) setUserUpdate({ ...targetUser });
    setIsOpenModal((prev) => {
      const newState = [...prev];
      newState[1] = true;
      return newState;
    });
  };

  const handleDialogClose = () => {
    setIsOpenModal([false, false]);
    setErrorMsg('');
    setIsOpenAccountUnlock(false);
  };

  /** アカウントロック解除のダイアログ表示 */
  const handleModalAccountUnlockOpen = (user: GetUserInfo) => {
    setUserUpdate(user);
    setIsOpenAccountUnlock(true);
  };

  /** アカウントロック解除の確認ダイアログのOK押下 */
  const onClickOkAccountUnlockModal = async () => {
    handleDialogClose();
    if (userUpdate) {
      const targetUser: PutUserInfo = {
        ...userUpdate,
        isLocked: false,
        password: '',
      };
      const result = await dispatch(updateUser(targetUser));
      if (isRejected(result)) {
        showApiError(result.payload, API_CODE.USER_INFO);
        return;
      }
      fetchUserList();
    }
  };

  const handleAction = (action: 'search' | 'clear') => {
    switch (action) {
      case 'search':
        fetchUserList();
        break;
      case 'clear':
        /** 検索パラメータのみクリア */
        const { sortKey, order } = params;
        setParams({ ...defaultParams, sortKey, order });
        break;
      default:
        console.error('Unknown action:', action);
    }
  };

  /** ダイアログを閉じて一覧を更新する */
  const handleRefreshTable = () => {
    handleDialogClose();
    fetchUserList();
  };

  /** ユーザー追加 */
  const handleUserRegist = async (postTargetUser: PostUserInfo) => {
    const response = await dispatch(createUser(postTargetUser));
    if (isRejected(response)) {
      setErrorMsg(getApiErrorMessage(response.payload, API_CODE.USER_INFO));
      return;
    }
    handleRefreshTable();
  };

  /** ユーザー更新 */
  const handleUserUpdate = async (putTargetUser: PutUserInfo) => {
    const response = await dispatch(updateUser(putTargetUser));
    if (isRejected(response)) {
      setErrorMsg(getApiErrorMessage(response.payload, API_CODE.USER_INFO));
      return;
    }
    handleRefreshTable();
  };

  /** ユーザー削除 */
  const handleUserDelete = async (userId: string) => {
    const response = await dispatch(deleteUser(userId));
    if (isRejected(response)) {
      showApiError(response.payload, API_CODE.USER_INFO);
      return;
    }
    handleRefreshTable();
  };

  return (
    <Box mt={8}>
      <Box>
        <UserSearchForm
          params={params}
          updateParams={setParams}
          handleAction={handleAction}
        />
        <UsersTable
          params={params}
          isAdmin={isAdmin}
          userList={userList}
          handleDialogUpdateOpen={handleDialogUpdateOpen}
          handleModalAccountUnlockOpen={handleModalAccountUnlockOpen}
          onSortChange={handleSortChange}
          handleUserDelete={handleUserDelete}
        />
        <ConfirmModal
          isOpen={isOpenAccountUnlock}
          displayMessage={t('MSG_UNLOCK_USER')}
          onClickCancel={() => setIsOpenAccountUnlock(false)}
          onClickOk={() => onClickOkAccountUnlockModal()}
        />
        {isAdmin && (
          <>
            <UserRegisterModal
              errorMsg={errorMsg}
              updateErrorMsg={setErrorMsg}
              isOpen={isOpenModal[0]}
              handleUserRegist={handleUserRegist}
              handleClose={handleDialogClose}
            />
            <UserEditModal
              errorMsg={errorMsg}
              updateErrorMsg={setErrorMsg}
              isOpen={isOpenModal[1]}
              userUpdate={userUpdate}
              handleUserUpdate={handleUserUpdate}
              handleDialogClose={handleDialogClose}
            />
            <AddButton onClick={handleDialogCreateOpen} />
          </>
        )}
        {error && <ErrorModal message={error} onClose={hideError} />}
      </Box>
    </Box>
  );
};

export default ManageUser;
